import { Input } from "../../../components/Input/Input";
import { NumberInputWithUnit } from "../../../components/NumberInputWithUnit/NumberInputWithUnit";
import { Switch } from "../../../components/Switch/Switch";
import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import type { BotsPopulationConfig } from "../config/types";
import { joinRateFill, spellSpan, SWEEP_MINUTES } from "./joinRate";
import styles from "./BotsSettings.module.css";

interface Props {
  value: BotsPopulationConfig;
  onChange: (patch: Partial<BotsPopulationConfig>) => void;
  dirty: boolean;
  /** `guild.max_members` of this project, so the target can say its ceiling. */
  guildMaxMembers: number;
}

/**
 * `bots.population` — where bots go: which guilds get topped up, how far, how
 * fast, and how they drift back out.
 *
 * Mockup: bots-page-shell.html §4 — SEVEN rows, plus «Refill pause after
 * kick» built on the pattern of the «Min stay / min rejoin» row above it.
 *
 * The Config page's Bots section carried a combined «Churn» row holding the
 * leave rate beside the two hour bounds. It is split, as the mockup draws it,
 * and the split is not cosmetic: a probability and a pair of durations are
 * different controls, the join rate opposite it is a probability with a preview
 * under it, and — the reason that decides it — the leave rate's own helper is
 * the only place in the product that states the founder exception. Folded into
 * a row about three fields at once, that sentence had nowhere to be and was
 * simply lost.
 */
export function PopulationSection({
  value,
  onChange,
  dirty,
  guildMaxMembers,
}: Props) {
  // What the WORKER actually fills, at the two ends of the target range.
  // `fillGuild` caps the seats it draws for by `FillRule.BotCap(g.Target)` —
  // `that guild's own target × max_bot_share / 100` — so a preview that counted
  // the target promised a fill nothing performs: at the defaults 24 seats of
  // 40, at the mockup's numbers 6 to 9 of 10 to 16. It is the same arithmetic
  // the Bot share cap helper two rows above prints, and the whole declared
  // position of this preview is that it is COMPUTED rather than drawn.
  const seatsMin = Math.floor(
    (value.target_members_min * value.max_bot_share) / 100,
  );
  const seatsMax = Math.floor(
    (value.target_members * value.max_bot_share) / 100,
  );
  // Equal ends are a project that never asked for a spread, and every sentence
  // in this card then says one number: «16 to 16» reads as a typo, not a range.
  const spread = value.target_members_min < value.target_members;
  const inverted = value.target_members_min > value.target_members;
  const belowThreshold = value.target_members < value.fill_below_members;
  const rangeErrID = "bots-target-range-err";
  const thresholdErrID = "bots-target-threshold-err";

  return (
    <SectionCard
      id="population"
      title="Population"
      apiName="bots.population"
      dirty={dirty}
    >
      <FieldRow
        name="Concentrate real players"
        apiName="bots.population.concentrate_real_players"
        hint="Recommendations put newcomers into the guild that already holds the most living players and still has room, instead of spreading them one per guild. It involves no bots at all and works with the simulation switched off — and it is off until you ask, because it changes what the guild feed returns."
      >
        <Switch
          checked={value.concentrate_real_players}
          onChange={(next) => onChange({ concentrate_real_players: next })}
          label={value.concentrate_real_players ? "On" : "Off"}
        />
      </FieldRow>

      <FieldRow
        name="Fill threshold"
        apiName="bots.population.fill_below_members"
        hint="A guild with fewer members than this gets topped up with bots."
      >
        <NumberInputWithUnit
          unit="members"
          value={value.fill_below_members}
          onChange={(n) => onChange({ fill_below_members: n })}
          min={0}
          aria-label="Fill threshold"
        />
      </FieldRow>

      <FieldRow
        name="Target size"
        apiName="bots.population.target_members_min / target_members"
        hint={`Bots stop joining at this size. Set the two ends apart and every guild draws its own target out of the range and keeps it: the quiet ones rest at 10, the busiest at 16, instead of the whole project resting on one number. Never above guild.max_members — currently ${guildMaxMembers}.`}
      >
        <div className={styles.pair}>
          <span className={styles.unit}>from</span>
          <Input
            className={`${styles.narrow} ${inverted ? styles.err : ""}`}
            type="number"
            value={String(value.target_members_min)}
            onChange={(e) =>
              onChange({ target_members_min: Number(e.target.value) })
            }
            min={1}
            aria-invalid={inverted || undefined}
            aria-describedby={inverted ? rangeErrID : undefined}
            aria-label="Smallest target size"
          />
          <span className={styles.unit}>to</span>
          <Input
            className={`${styles.narrow} ${belowThreshold ? styles.err : ""}`}
            type="number"
            value={String(value.target_members)}
            onChange={(e) => {
              const next = Number(e.target.value);
              // While the two ends are ONE number, they move as one number.
              // A project that never spread its guilds has a single target,
              // and making its operator edit two boxes to change it — or
              // refusing the save until they do — would be a new chore in
              // return for a range they did not ask for. Spreading is done
              // from the left-hand box, which is what the helper says.
              //
              // Equality and not «no spread»: an inverted range is not a
              // spread either, and carrying the lower end along there would
              // silently throw away the number the operator has just typed
              // into the box the error is pointing at.
              onChange(
                value.target_members_min === value.target_members
                  ? { target_members: next, target_members_min: next }
                  : { target_members: next },
              );
            }}
            min={1}
            aria-invalid={belowThreshold || undefined}
            aria-describedby={belowThreshold ? thresholdErrID : undefined}
            aria-label="Target size"
          />
          <span className={styles.unit}>members</span>
        </div>
        {inverted && (
          <div className={styles.fieldError} id={rangeErrID}>
            The smallest guild cannot be larger than the largest.
          </div>
        )}
        {belowThreshold && (
          <div className={styles.fieldError} id={thresholdErrID}>
            Below the fill threshold — bots would join and leave forever.
          </div>
        )}
        {!inverted && !belowThreshold && (
          <div className={styles.helper}>
            {spread
              ? "A guild's own target never changes — it follows the guild, not the sweep — so the same guild is the same size tomorrow. Widen the range and every guild in the project re-spreads at once, including the ones already standing."
              : `Every guild in the project rests at the same ${value.target_members}. Lower the left-hand end to spread them out.`}
          </div>
        )}
      </FieldRow>

      <FieldRow
        name="Bot share cap"
        apiName="bots.population.max_bot_share"
        hint="Hard ceiling per guild, measured against the target size: at 60% of a target of 40, a guild holds at most 24 bots however empty it is. Measured against the CURRENT roster it would be unusable — 60% of one living player is nobody."
      >
        <NumberInputWithUnit
          unit="%"
          value={value.max_bot_share}
          onChange={(n) => onChange({ max_bot_share: n })}
          min={0}
          max={100}
          aria-label="Bot share cap"
        />
        <div className={styles.helper}>
          {spread ? "Of each guild's own target size" : "Of the target size"} —{" "}
          {seatsMin < seatsMax
            ? `between ${seatsMin} and ${seatsMax} bots`
            : `at most ${seatsMax} bots`}{" "}
          in a guild.
        </div>
      </FieldRow>

      <FieldRow
        name="Join rate"
        apiName="bots.population.join_probability"
        hint={`The chance one empty seat is taken by a bot on a population sweep. Sweeps run every ${SWEEP_MINUTES} minutes.`}
      >
        <span className={styles.unitWrap}>
          <Input
            className={styles.number}
            type="number"
            value={String(value.join_probability)}
            onChange={(e) =>
              onChange({ join_probability: Number(e.target.value) })
            }
            aria-label="Join rate"
          />
          <span className={styles.unit}>% per seat, per sweep</span>
        </span>
        <RatePreview
          percent={value.join_probability}
          seatsMin={seatsMin}
          seatsMax={seatsMax}
          targetMin={value.target_members_min}
          targetMax={value.target_members}
        />
      </FieldRow>

      <FieldRow
        name="Leave rate"
        apiName="bots.population.leave_probability"
        hint="Per sweep, per bot. Small numbers on purpose: a guild whose roster reshuffles daily reads as fake."
      >
        <span className={styles.unitWrap}>
          <Input
            className={styles.number}
            type="number"
            value={String(value.leave_probability)}
            onChange={(e) =>
              onChange({ leave_probability: Number(e.target.value) })
            }
            aria-label="Leave rate"
          />
          <span className={styles.unit}>% per bot, per sweep</span>
        </span>
        {/*
          The two bounds on this field, said where the field is — and the
          second of them is the ONLY place in the product the founder exception
          appears at all. Everywhere else it lives in docs/bots.md, and a
          prohibition whose exception is written down somewhere else is a
          prohibition somebody reads as a bug a year later.
        */}
        <div className={styles.helper}>
          Bots drift out of guilds the way people do. A guild is never taken
          below the fill threshold, and the founder of a guild with no living
          player in it never leaves — nobody could take the seat, so the first
          roll of the dice would dissolve a showcase created a minute earlier.
          It rejoins the draw as soon as one living player is there to inherit.
        </div>
      </FieldRow>

      <FieldRow
        name="Min stay / min rejoin"
        apiName="bots.population.min_stay_hours / min_rejoin_hours"
        hint="How long a bot sits in a guild before it may leave, and how long it waits before joining another."
      >
        <div className={styles.pair}>
          <span className={styles.unit}>stay ≥</span>
          <Input
            className={styles.narrow}
            type="number"
            value={String(value.min_stay_hours)}
            onChange={(e) =>
              onChange({ min_stay_hours: Number(e.target.value) })
            }
            aria-label="Minimum stay"
          />
          <span className={styles.unit}>h</span>
          <span className={styles.unit}>rejoin ≥</span>
          <Input
            className={styles.narrow}
            type="number"
            value={String(value.min_rejoin_hours)}
            onChange={(e) =>
              onChange({ min_rejoin_hours: Number(e.target.value) })
            }
            aria-label="Minimum rejoin"
          />
          <span className={styles.unit}>h</span>
        </div>
      </FieldRow>

      <FieldRow
        name="Refill pause after kick"
        apiName="bots.population.kick_refill_pause_hours"
        hint="After a bot is kicked from a guild, that guild gets no new bots for this many hours. The kicked bot never returns to that guild, whatever this is set to; 0 turns the pause off."
      >
        <div className={styles.pair}>
          <Input
            className={styles.narrow}
            type="number"
            value={String(value.kick_refill_pause_hours)}
            onChange={(e) =>
              onChange({ kick_refill_pause_hours: Number(e.target.value) })
            }
            aria-label="Refill pause after kick"
          />
          <span className={styles.unit}>h</span>
        </div>
      </FieldRow>
    </SectionCard>
  );
}

/**
 * "9" while the two ends agree and "6 to 9" when they do not — every sentence
 * in this card is built through it, because "9 to 9" reads as a typo rather
 * than as a range.
 */
function span(lo: number, hi: number): string {
  return lo < hi ? `${lo} to ${hi}` : String(hi);
}

/**
 * What the join rate MEANS, as a bar with three ticks on it.
 *
 * The seats are what the WORKER fills — the bot share cap, not the target —
 * and every sentence here is about those seats. The rest of the target is not
 * slow, it is not for bots at all, and a preview that counted it would promise
 * a fill the worker never performs and a duration (`H_target / p`) longer than
 * the one it walks.
 *
 * Both ends of the target range are drawn, because they fill at different
 * speeds: the ticks and the bar belong to the largest guild, which is the one
 * still filling when the smallest has finished. Where the two ends agree every
 * sentence collapses to one number, since a project that never spread its
 * guilds must not be told about a range it does not have.
 *
 * The arithmetic is `joinRateFill` and is unit-tested there; everything here
 * is the drawing. The bar's x-axis is time, so the accent stretch runs from
 * zero to the "half the seats" tick and the last tick sits at its end.
 */
function RatePreview({
  percent,
  seatsMin,
  seatsMax,
  targetMin,
  targetMax,
}: {
  percent: number;
  /** `target × max_bot_share / 100` at the two ends — the seats bots may take
   *  in the smallest guild of the project and in the largest. */
  seatsMin: number;
  seatsMax: number;
  /** The target range, for the line that says who the other seats are for. */
  targetMin: number;
  targetMax: number;
}) {
  const guild = `A new guild's ${span(seatsMin, seatsMax)} bot ${
    seatsMax === 1 ? "seat" : "seats"
  }`;
  // The seats the cap leaves to living players. Said once, where the number
  // stops matching the target size two rows above and would otherwise read as
  // an arithmetic mistake.
  const restMin = Math.max(0, targetMin - seatsMin);
  const restMax = Math.max(0, targetMax - seatsMax);
  const others =
    restMax > 0
      ? ` The other ${span(restMin, restMax)} ${restMax === 1 ? "seat" : "seats"} of ${targetMin < targetMax ? "a" : "the"} target of ${span(targetMin, targetMax)} ${restMax === 1 ? "is" : "are"} for living players — the bot share cap holds them open.`
      : "";

  // A cap of zero is not a slow fill, it is no fill: `Math.floor` of any
  // target at 0% is zero seats, and joinRateFill would draw a curve for the
  // one seat it floors up to.
  if (seatsMax <= 0) {
    return (
      <div className={styles.ratePreview}>
        <div className={styles.rpTitle}>
          No seat in a guild may be held by a bot — the bot share cap is{" "}
          {targetMax > 0 ? "below one seat of the target" : "zero"}.
        </div>
        <div className={styles.rpSubBare}>
          The join rate changes nothing while that is true. Raise the bot share
          cap above zero and this preview says how long the seats it opens take
          to fill.
        </div>
      </div>
    );
  }

  const fill = joinRateFill(percent, seatsMax);

  if (fill.kind === "never") {
    return (
      <div className={styles.ratePreview}>
        <div className={styles.rpTitle}>
          {guild} never fill — at 0% no empty seat is ever taken.
        </div>
        <div className={styles.rpSubBare}>
          Bots already seated stay where they are. Raise this above zero to let
          the population worker start filling again.
        </div>
      </div>
    );
  }

  if (fill.kind === "one_sweep") {
    return (
      <div className={styles.ratePreview}>
        <div className={styles.rpTitle}>
          {guild} are taken on the first sweep — every one at once.
        </div>
        <div className={styles.rpSubBare}>
          That is how the simulation behaved before this field was read. Lower
          it to let a guild fill the way a real one does.{others}
        </div>
      </div>
    );
  }

  // The smallest guild's own fill. A cap that leaves it no seat at all leaves
  // it no duration either, so the span then falls back to the largest guild
  // and every sentence below reads as a single time.
  const low = joinRateFill(percent, seatsMin >= 1 ? seatsMin : seatsMax);
  // Both ends share a kind — it is decided by the rate alone — and the fallback
  // is what says so to the compiler.
  const lowAll = low.kind === "curve" ? low.all : fill.all;
  const time = spellSpan(lowAll, fill.all);

  return (
    <div className={styles.ratePreview}>
      <div className={styles.rpTitle}>
        {guild} fill in about <b>{time.joined}</b>
      </div>
      <div className={styles.rpBar}>
        <span
          className={styles.rpFill}
          style={{ width: `${fill.half.percent}%` }}
        />
        {[fill.half, fill.fourFifths, fill.all].map((tick) => (
          <span
            key={tick.short + tick.percent}
            className={styles.rpTick}
            style={{ left: `${tick.percent}%` }}
          >
            <span className={styles.rpTickLabel}>{tick.short}</span>
          </span>
        ))}
      </div>
      <div className={styles.rpSub}>
        Half of them in {fill.half.long}, four fifths in {fill.fourFifths.long},{" "}
        {time.joined === fill.all.long
          ? `the last one in ${fill.all.long}`
          : `the last one between ${lowAll.long} in the smallest guild and ${time.tail} in the largest`}{" "}
        — fast at first, slow at the end, the way a real guild fills. Set it to
        100 to fill in one sweep.{others}
      </div>
    </div>
  );
}
